/* eslint-disable react/jsx-no-bind */
import React, { memo, useCallback, useContext, useEffect, useMemo } from 'react';

import { Prompt } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

import { setDataDetail } from '@actions/data-detail-actions';

import BatchContext from '@components/batch/batch-upload-edit/batch-context';
import ErrorIdExists from '@components/batch/batch-upload-edit/error-id-exists';
import SkippedWarning from '@components/batch/batch-upload-edit/skipped-warning';
import FormTab from '@components/entity/info/drawer/form/form-tab';
import NonFieldErrors from '@components/entity/info/drawer/form/non-field-errors';
import Map from '@components/entity/info/map';
import ParamsContext from '@components/entity/info/params-context';

import { paper } from '@constants/mui-theme';

import { getBatchRow } from '@selectors/batch-edit-selector';

import EditScheduleDialog from '@shared/dialogs/edit-schedule-dialog';
import BulkScheduleDialog from '@shared/dialogs/bulk-schedule-dialog';
import ViewScheduleDialog from '@shared/dialogs/view-schedule-dialog';

import { onScroll } from '@utils/form-utils';
import { batchRouterWillLeave } from '@utils/router-utils';

import './content-form.scss';
import styles from '../../forms/forms.scss';  // eslint-disable-line css-modules/no-unused-class

// Style overrides to apply in this batch upload edit page, to be able to
// reuse the form component.
const batchEditFormStyleOverride = {
  // For the entity page, maxWidth is "$data-details-tabbed-max-width",
  // which is 51.875rem. But in this batch edit page we don't have
  // the form section's tabs, so we can substract that, that's why
  // we use 39.375rem here.
  maxWidth: '39.375rem',
  // Also with should be 100%, we don't need to subtract that,
  // because of that section.
  width: '100%'
};

const ContentForm = () => {
  const dispatch = useDispatch();
  const { batch: { entity }, fileId, rowId } = useContext(BatchContext);
  const { details } = useSelector(state => state.config);
  const { modified } = useSelector(state => state.dataDetail);
  const { discard } = useSelector(state => state.confirmation);
  const dataType = useMemo(() => entity, [entity]);
  const contextValue = useMemo(() => ({ dataType, id: rowId }), [dataType, rowId]);
  const batchRow = useSelector(state => getBatchRow(state, fileId, rowId));

  useEffect(() => {
    if (rowId && batchRow) {
      // Load from batchEdit into dataDetail.
      dispatch(setDataDetail(dataType, batchRow.data, batchRow.errors));
    }
    // We only check if the batch row version changed, in order to know if we should
    // load dataDetails with it (since we already sync between them with middleware,
    // this prevents additional re-renderings or an infinite one).
  }, [dispatch, fileId, rowId, dataType, batchRow?.version]);  // eslint-disable-line react-hooks/exhaustive-deps

  const tabs = useMemo(() => details?.[dataType]?.tabs.filter(tab => !tab.hide_internal), [dataType, details]);
  const onFormScroll = useCallback(() => onScroll(tabs), [tabs]);

  return (
    <div styleName="batch-upload-edit-content-wrapper">
      <div className={styles.drawerFormContainer} key="drawer" style={{...paper, ...batchEditFormStyleOverride}}>
        <div styleName="batch-upload-edit-content-form">
          <div styleName="batch-upload-edit-content-form-container">
            <ParamsContext.Provider value={contextValue}>
              <div id="form-container" onScroll={onFormScroll} className={styles.formContainer}>
                <ErrorIdExists dataType={dataType} />
                <SkippedWarning />
                <NonFieldErrors />
                {tabs?.map(tab => <FormTab key={tab.id} tab={tab} />)}
                <BulkScheduleDialog />
                <ViewScheduleDialog />
                <EditScheduleDialog />
                <Prompt when={modified && !discard} message={nextLocation => batchRouterWillLeave(dispatch, nextLocation, window.location)} />
              </div>
            </ParamsContext.Provider>
          </div>
        </div>
      </div>
      <Map key={rowId} />
    </div>
  );
};

export default memo(ContentForm);
